import localeUtils from 'utils/LanguageLocale';
import DateEN from 'utils/locales/Date_en_US';
import DateFR from 'utils/locales/Date_fr_CA';
import contentConstants from 'constants/content';
import Cookies from 'utils/Cookies';
import anaConsts from 'analytics/constants';

const { COMPONENT_HANDLER_TYPES } = contentConstants;

const getDateText = localeUtils.getLocalisedDataFromResource(DateEN, DateFR);

export const BI_PROGRAM_FAQS_URL = '/beauty/loyalty-program';

// Activity type codes
export const ACTIVITY_TYPE_CODE = {
    GAME_POINT_CREDIT: 'GAME_POINT_CREDIT',
    GAME_POINT_DEBIT: 'GAME_POINT_DEBIT'
};

// Beauty Insider activity icon sources
export const ACTIVITY_ICON_SRC = {
    cashApplied: '/img/ufe/icons/points-cash.svg',
    gamification: '/img/ufe/bi-gamification-icon.svg'
};

export const isActivityGroupingEnabled = () => {
    if (localeUtils.isCanada()) {
        return Sephora.configurationSettings.loyalty?.enableCreativeRefreshP1_CA;
    }

    return Sephora.configurationSettings.loyalty?.enableCreativeRefreshP1_US;
};

export const shouldDisableAllRewardButtons = basket => {
    const lowerPointLimit = localeUtils.isCanada()
        ? Sephora.configurationSettings.loyalty?.rewardBundling_CA?.thresholds?.lowerPointLimit
        : Sephora.configurationSettings.loyalty?.rewardBundling_US?.thresholds?.lowerPointLimit;

    if (lowerPointLimit === null || lowerPointLimit === undefined) {
        return false;
    }

    // Defer the gate until basket points hydrate — the initial basket reducer state
    // has no availableBiPoints, and treating that as 0 would flash all buttons off.
    if (basket?.availableBiPoints === undefined || basket?.availableBiPoints === null) {
        return false;
    }

    const remaining = basket.availableBiPoints - (basket?.redeemedBiPoints || 0);

    return remaining <= lowerPointLimit;
};

/**
 * Determines whether the Reward Bundling banner should be shown, gated by the
 * ConfigHub ops toggle `loyalty.rewardBundling_{US,CA}.isEnabled`. Defaults to
 * false (safe default) when the config key is missing or unavailable.
 *
 * @returns {boolean} Whether Reward Bundling is enabled for the current locale.
 */
export const isRewardBundlingEnabled = () => {
    if (localeUtils.isCanada()) {
        return !!Sephora.configurationSettings.loyalty?.rewardBundling_CA?.isEnabled;
    }

    return !!Sephora.configurationSettings.loyalty?.rewardBundling_US?.isEnabled;
};

/**
 * Counts the number of distinct 100-point reward SKUs in the Ship to Home basket.
 * Walks the real nested basket shape: basket.itemsByBasket[].items[].sku.{skuId,biType}
 * with basket-level basketType of 'ShipToHome' | 'SameDay' | 'Pickup' (verified against
 * itemsByBasketSelector / withOrderSummaryProps.js).
 *
 * @param {Array} itemsByBasket - basket.itemsByBasket array of per-basket-type groups.
 * @returns {number} Count of distinct SKU IDs found in the Ship to Home basket.
 */
export const countDistinctSthRewards = itemsByBasket => {
    const STH_BASKET_TYPE = 'ShipToHome';
    const REWARD_BI_TYPE = '100 Points';

    if (!Array.isArray(itemsByBasket)) {
        return 0;
    }

    const sthBasket = itemsByBasket.find(basket => basket.basketType === STH_BASKET_TYPE);

    if (!sthBasket || !Array.isArray(sthBasket.items)) {
        return 0;
    }

    const rewardSkuIds = sthBasket.items.filter(item => item.sku && item.sku.biType === REWARD_BI_TYPE && item.sku.skuId).map(item => item.sku.skuId);

    return new Set(rewardSkuIds).size;
};

export const isCreativeRefreshPhase2Enabled = (validatePhase1 = true) => {
    if (validatePhase1 && !isActivityGroupingEnabled()) {
        return false;
    }

    const isPhase2ConfigEnabled = localeUtils.isCanada()
        ? Sephora.configurationSettings.loyalty?.creativeRefreshP2_CA?.isEnabled
        : Sephora.configurationSettings.loyalty?.creativeRefreshP2_US?.isEnabled;

    if (!isPhase2ConfigEnabled) {
        return false;
    }

    if (Cookies.getRCPSBIHUBCookieValue() === null) {
        return true;
    }

    return Cookies.isRCPSBIHUBEnabled();
};

// Lazy lookups — built on first call, not at module load. Avoids a circular
// dependency crash when this module is loaded before analytics/constants
// finishes executing (e.g. via Sku.js → analytics/utils → analytics/constants).
let bihubContextCarouselNames;
const getBihubContextCarouselNames = () => {
    if (!bihubContextCarouselNames) {
        bihubContextCarouselNames = {
            [anaConsts.CONTEXT.BASKET_NEW_REWARDS]: anaConsts.CAROUSEL_NAMES.NEW_REWARDS,
            [anaConsts.CONTEXT.BASKET_SPOTLIGHT_REWARDS]: anaConsts.CAROUSEL_NAMES.BRAND_SPOTLIGHT,
            [anaConsts.CONTEXT.BASKET_BIRTHDAY_GIFT_REWARDS]: anaConsts.CAROUSEL_NAMES.BIRTHDAY_GIFT_REWARDS
        };
    }

    return bihubContextCarouselNames;
};

/**
 * Resolves the P2 eVar75 carousel name for a BI Hub analytics context.
 * Returns a constant name for fixed contexts, carouselName for BASKET_REWARDS, or null for non-BI-Hub contexts.
 */
export const getBiHubP2CarouselName = (analyticsContext, carouselName) =>
    getBihubContextCarouselNames()[analyticsContext] || (analyticsContext === anaConsts.CONTEXT.BASKET_REWARDS ? carouselName : null);

let rewardsSectionContexts;
const getRewardsSectionContexts = () => {
    if (!rewardsSectionContexts) {
        rewardsSectionContexts = {
            [COMPONENT_HANDLER_TYPES.PRODUCT_LIST.NEW_REWARDS]: anaConsts.CONTEXT.BASKET_NEW_REWARDS,
            [COMPONENT_HANDLER_TYPES.PRODUCT_LIST.BRAND_SPOTLIGHT_REWARDS]: anaConsts.CONTEXT.BASKET_SPOTLIGHT_REWARDS
        };
    }

    return rewardsSectionContexts;
};

// Removes out-of-stock skus (keeping coming-soon treatments) from a rewards section's skuList.
const filterRewardsSectionSkuList = item => {
    const filteredSkuList = item.skuList.filter(sku => sku.isComingSoonTreatment || !sku.isOutOfStock);

    return filteredSkuList.length !== item.skuList.length ? { ...item, skuList: filteredSkuList } : item;
};

// Tags a rewards section with its analytics context and the isBIRBReward flag.
const addRewardsSectionAnalyticsContext = (item, analyticsContext) => ({
    ...item,
    analyticsContext,
    isBIRBReward: true
});

// Adds analytics context to reward sections. OOS SKUs are removed only from New Rewards;
// Brand Spotlight retains them so sortBrandSpotlightSkus can push them to the back.
export const filterRewardsSectionsSkuList = content => {
    if (!Array.isArray(content)) {
        return [];
    }

    return content.map(item => {
        if (!Array.isArray(item.skuList)) {
            return item;
        }

        const rewardsHandlerType = Array.isArray(item.features)
            ? item.features.find(f => getRewardsSectionContexts()[f?.handlerType])?.handlerType
            : null;

        if (!rewardsHandlerType) {
            return item;
        }

        const shouldFilterOos = rewardsHandlerType === COMPONENT_HANDLER_TYPES.PRODUCT_LIST.NEW_REWARDS;
        const withFilteredSkus = shouldFilterOos ? filterRewardsSectionSkuList(item) : item;

        return addRewardsSectionAnalyticsContext(withFilteredSkus, getRewardsSectionContexts()[rewardsHandlerType]);
    });
};

/**
 * Sorts the Brand Spotlight skuList within the Contentful content array.
 *
 * Sort order:
 *   1. Available SKUs (in-stock + coming soon) — sorted by points ascending.
 *      Tiebreaker within same points tier: in-stock ("Add") before coming soon.
 *   2. OOS SKUs (isOutOfStock && !isComingSoonTreatment) — pushed to
 *      the back, sorted by points ascending.
 *
 * listPrice arrives as a formatted string (e.g. "100 Points") so we extract the
 * leading digit sequence via regex rather than relying on parseInt's implicit stopping.
 *
 * @param {Array} content - Contentful content array containing section items with a `features` array and `skuList`.
 * @returns {Array} The same content array with the Brand Spotlight skuList re-ordered,
 *                  or the original array unchanged when the section is absent.
 */
export const sortBrandSpotlightSkus = content => {
    if (!Array.isArray(content)) {
        return [];
    }

    const isBrandSpotlight = item =>
        Array.isArray(item.features) && item.features.some(f => f?.handlerType === COMPONENT_HANDLER_TYPES.PRODUCT_LIST.BRAND_SPOTLIGHT_REWARDS);
    const spotlightItem = content.find(item => isBrandSpotlight(item) && Array.isArray(item.skuList));

    if (!spotlightItem) {
        return content;
    }

    // listPrice is a formatted string like "100 Points" — extract the numeric part.
    const parsePoints = listPrice => {
        const match = String(listPrice).match(/\d+/);

        return match ? parseInt(match[0], 10) : 0;
    };

    // availableSkus: primary sort by points asc; tiebreaker puts in-stock (0) before coming-soon (1).
    const sortAvailable = arr =>
        [...arr].sort((a, b) => parsePoints(a.listPrice) - parsePoints(b.listPrice) || (a.isOutOfStock ? 1 : 0) - (b.isOutOfStock ? 1 : 0));

    // oosSkus: sort by points asc only.
    const sortByPoints = arr => [...arr].sort((a, b) => parsePoints(a.listPrice) - parsePoints(b.listPrice));

    // OOS: out-of-stock without a coming-soon treatment — these go to the back.
    const isOos = sku => sku.isOutOfStock && !sku.isComingSoonTreatment;
    const availableSkus = spotlightItem.skuList.filter(sku => !isOos(sku));
    const oosSkus = spotlightItem.skuList.filter(isOos);

    const sortedSkuList = [...sortAvailable(availableSkus), ...sortByPoints(oosSkus)];

    return content.map(item => (isBrandSpotlight(item) ? { ...item, skuList: sortedSkuList } : item));
};

// Gamification Activity (BI Challenge)
export const isGamificationActivity = activityTypeCode => {
    return activityTypeCode === ACTIVITY_TYPE_CODE.GAME_POINT_CREDIT || activityTypeCode === ACTIVITY_TYPE_CODE.GAME_POINT_DEBIT;
};

/**
 * Formats a currency value for the appropriate locale.
 * For the French locale, moves the dollar sign to the end (e.g. "$20" → "20 $").
 * Returns a placeholder when no value is provided.
 *
 * @param {string} value - Currency string to format (e.g. "$20").
 * @param {boolean} isFrench - Whether to apply French Canadian formatting.
 * @returns {string} Formatted currency string.
 */
export const formatValue = (value, isFrench) => {
    if (!value) {
        return isFrench ? '-- $' : '$--';
    }

    // Value comes as "$20" or similar, convert for French
    if (isFrench) {
        // Remove $ and add it after the number
        const amount = value.replace('$', '');

        return `${amount} $`;
    }

    return value;
};

/**
 * Formats the expiration date to display as "Aug 1" (EN) or "1 août" (FR)
 * @param {string} dateString - ISO date string (e.g., "2026-06-01T23:59:59-07:00")
 * @param {boolean} isFrench - Whether to format for French locale
 * @returns {string} Formatted date string
 */
export const formatExpirationDate = (dateString, isFrench) => {
    const locale = isFrench ? 'fr-CA' : 'en-US';
    const options = { month: 'short', day: 'numeric', timeZone: 'UTC' };
    const dateMatch = dateString && dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!dateMatch) {
        return '';
    }

    const [, year, month, day] = dateMatch;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

    return date.toLocaleDateString(locale, options);
};

/**
 * Formats expiration date for display using SDL-managed month names.
 * Extracts the date portion directly from the ISO string to avoid timezone-dependent shifts
 * (e.g. "2026-08-30T23:59:59-07:00" always displays as "Aug 30", regardless of browser timezone).
 * @param {string} expirationDate - ISO date string (e.g., "2026-08-30T23:59:59-07:00")
 * @param {boolean} isFrench - Whether to format for French locale
 * @returns {string} Formatted date like "Aug 30" or "30 août" and '' if the date is invalid
 */
export const formatExpirationLocalized = (expirationDate, isFrench) => {
    const dateMatch = expirationDate && expirationDate.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!dateMatch) {
        return '';
    }

    const [, year, month, day] = dateMatch;

    const monthNames = getDateText('SHORTENED_MONTHS');
    const monthName = monthNames[Number(month) - 1];
    let yearNum = Number(year);

    if (!yearNum) {
        yearNum = '';
    }

    if (!monthName) {
        return '';
    }

    return isFrench ? `${Number(day)} ${monthName.toLowerCase()}, ${yearNum}` : `${monthName} ${Number(day)}, ${yearNum}`;
};
